"use client"

import { useState } from "react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2, Send, Star } from "lucide-react"
import { useCases, feasibilityReviews } from "@/lib/mock-data"
import { FeasibilityBadge, StatusBadge } from "@/components/status-badges"
import type { FeasibilityReview, ReviewTeam, ReviewStatus } from "@/lib/types"

const reviewTeamFocus: Record<ReviewTeam, string[]> = {
  SOA: ["Integration requirements", "API availability", "Enterprise architecture fit", "Source systems"],
  Infra: ["Hosting requirements", "Compute requirements", "Storage", "GPU / CPU needs", "Deployment model"],
  Network: ["Connectivity", "Firewall rules", "Routing", "VPN", "External API access"],
  "IT-Ops": ["Monitoring", "Logging", "Support model", "Incident handling", "Production readiness"],
}

const reviewStatusOptions: ReviewStatus[] = [
  "Feasible",
  "Feasible with Conditions",
  "Not Feasible",
  "More Info Required",
]

const reviewTeams: ReviewTeam[] = ["SOA", "Infra", "Network", "IT-Ops"]

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="transition-colors"
        >
          <Star
            className={`size-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
          />
        </button>
      ))}
      <span className="text-xs text-muted-foreground ml-1 self-center">{value}/5</span>
    </div>
  )
}

type ReviewFormState = {
  useCaseId: string
  reviewStatus: ReviewStatus | ""
  feasibilityRating: number
  dependencies: string
  blockers: string
  comments: string
  reviewedBy: string
}

function ReviewTab({ team }: { team: ReviewTeam }) {
  const pendingUseCases = useCases.filter(
    (uc) => uc.status === "Feasibility Pending" || uc.status === "AI Mapped" || uc.status === "Feasibility Completed"
  )

  const [form, setForm] = useState<ReviewFormState>({
    useCaseId: "",
    reviewStatus: "",
    feasibilityRating: 3,
    dependencies: "",
    blockers: "",
    comments: "",
    reviewedBy: `${team} Reviewer`,
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState<string[]>([])

  function setField<K extends keyof ReviewFormState>(k: K, v: ReviewFormState[K]) {
    setForm((prev) => ({ ...prev, [k]: v }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.useCaseId || !form.reviewStatus) {
      toast.error("Please select a use case and review status.")
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch("http://95.217.189.103:5000/api/feasibility-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          reviewTeam: team,
          dependencies: form.dependencies.split(",").map((s) => s.trim()).filter(Boolean),
          blockers: form.blockers.split(",").map((s) => s.trim()).filter(Boolean),
          reviewDate: new Date().toISOString().split("T")[0],
        }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`${team} review submitted. Overall: ${data.overallFeasibility}`)
        setSubmitted((prev) => [...prev, form.useCaseId])
        setForm((prev) => ({ ...prev, useCaseId: "", reviewStatus: "", comments: "", dependencies: "", blockers: "" }))
      }
    } catch {
      toast.error("Submission failed.")
    } finally {
      setSubmitting(false)
    }
  }

  const existingReviews = feasibilityReviews.filter((r) => r.reviewTeam === team)

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Focus areas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">{team} Review Focus Areas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {reviewTeamFocus[team].map((area) => (
              <Badge key={area} variant="secondary" className="text-xs">{area}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending use cases */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Pending Use Cases for {team} Review</CardTitle>
          <CardDescription className="text-xs">{pendingUseCases.length} use case(s) awaiting review</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2.5">ID</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Name</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Department</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Review Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingUseCases.map((uc) => {
                  const review = existingReviews.find((r) => r.useCaseId === uc.id)
                  const isDone = submitted.includes(uc.id) || !!review
                  return (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5">
                        <span className="text-xs font-mono text-muted-foreground">{uc.id}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-medium">{uc.name}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-muted-foreground">{uc.department}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge value={uc.status} />
                      </td>
                      <td className="px-4 py-2.5">
                        {review ? (
                          <span className={`text-xs px-2 py-0.5 rounded border font-medium
                            ${review.reviewStatus === "Feasible" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                              review.reviewStatus === "Not Feasible" ? "bg-red-100 text-red-800 border-red-200" :
                              "bg-amber-100 text-amber-800 border-amber-200"}`}>
                            {review.reviewStatus}
                          </span>
                        ) : isDone ? (
                          <Badge variant="secondary" className="text-xs">Submitted</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">Pending</Badge>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Review Form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Submit {team} Review</CardTitle>
          <CardDescription className="text-xs">Complete the review form for a use case.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Use Case <span className="text-destructive">*</span></label>
                <Select value={form.useCaseId} onValueChange={(v) => setField("useCaseId", v)}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select use case" />
                  </SelectTrigger>
                  <SelectContent>
                    {pendingUseCases.map((uc) => (
                      <SelectItem key={uc.id} value={uc.id}>
                        {uc.id} — {uc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Review Status <span className="text-destructive">*</span></label>
                <Select value={form.reviewStatus} onValueChange={(v) => setField("reviewStatus", v as ReviewStatus)}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {reviewStatusOptions.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Feasibility Rating</label>
              <StarRating value={form.feasibilityRating} onChange={(v) => setField("feasibilityRating", v)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Dependencies</label>
                <Input
                  placeholder="Comma-separated list"
                  value={form.dependencies}
                  onChange={(e) => setField("dependencies", e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Blockers</label>
                <Input
                  placeholder="Comma-separated list (leave blank if none)"
                  value={form.blockers}
                  onChange={(e) => setField("blockers", e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Comments</label>
              <Textarea
                placeholder={`Provide your ${team} review comments...`}
                rows={3}
                value={form.comments}
                onChange={(e) => setField("comments", e.target.value)}
                className="text-sm resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Reviewed By</label>
                <Input
                  value={form.reviewedBy}
                  onChange={(e) => setField("reviewedBy", e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submitting} className="gap-2">
                {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                Submit Review
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function overallFeasibility(reviews: FeasibilityReview[]): { label: string; color: string } {
  if (reviews.length === 0) return { label: "Pending", color: "text-muted-foreground" }
  if (reviews.some((r) => r.reviewStatus === "Not Feasible")) return { label: "Red", color: "text-red-700" }
  if (reviews.some((r) => r.reviewStatus === "Feasible with Conditions" || r.reviewStatus === "More Info Required"))
    return { label: "Amber", color: "text-amber-700" }
  return { label: "Green", color: "text-emerald-700" }
}

function ConsolidatedView() {
  return (
    <div className="p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Consolidated Feasibility View</CardTitle>
          <CardDescription className="text-xs">Overall feasibility status across all review teams.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground px-5 py-3">Use Case</th>
                  {reviewTeams.map((t) => (
                    <th key={t} className="text-left text-xs font-medium text-muted-foreground px-4 py-3">{t}</th>
                  ))}
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Overall</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Key Blockers</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {useCases.map((uc) => {
                  const ucReviews = feasibilityReviews.filter((r) => r.useCaseId === uc.id)
                  const overall = overallFeasibility(ucReviews)
                  const allBlockers = ucReviews.flatMap((r) => r.blockers)
                  return (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium">{uc.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{uc.id}</span>
                        </div>
                      </td>
                      {reviewTeams.map((t) => {
                        const r = ucReviews.find((rev) => rev.reviewTeam === t)
                        return (
                          <td key={t} className="px-4 py-3">
                            {r ? (
                              <span className={`text-xs px-1.5 py-0.5 rounded border font-medium
                                ${r.reviewStatus === "Feasible" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                                  r.reviewStatus === "Not Feasible" ? "bg-red-100 text-red-800 border-red-200" :
                                  r.reviewStatus === "Feasible with Conditions" ? "bg-amber-100 text-amber-800 border-amber-200" :
                                  "bg-gray-100 text-gray-700 border-gray-200"}`}>
                                {r.reviewStatus === "Feasible" ? "Feasible" :
                                  r.reviewStatus === "Feasible with Conditions" ? "With Cond." :
                                  r.reviewStatus === "Not Feasible" ? "Not Feasible" : "More Info"}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        )
                      })}
                      <td className="px-4 py-3">
                        <FeasibilityBadge value={overall.label === "Green" ? "Green" : overall.label === "Red" ? "Red" : overall.label === "Amber" ? "Amber" : "Amber"} />
                      </td>
                      <td className="px-4 py-3">
                        {allBlockers.length > 0 ? (
                          <span className="text-xs text-destructive">{allBlockers.slice(0, 2).join(", ")}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="outline" className="text-xs h-7">
                          View Details
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function FeasibilityReviewContent() {
  return (
    <div className="p-0">
      <Tabs defaultValue="SOA">
        <div className="border-b border-border px-6 pt-4">
          <TabsList className="h-9">
            {reviewTeams.map((t) => (
              <TabsTrigger key={t} value={t} className="text-xs">{t} Review</TabsTrigger>
            ))}
            <TabsTrigger value="consolidated" className="text-xs">Consolidated View</TabsTrigger>
          </TabsList>
        </div>
        {reviewTeams.map((t) => (
          <TabsContent key={t} value={t} className="mt-0">
            <ReviewTab team={t} />
          </TabsContent>
        ))}
        <TabsContent value="consolidated" className="mt-0">
          <ConsolidatedView />
        </TabsContent>
      </Tabs>
    </div>
  )
}
